import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, RotateCcw, Check, ChevronRight, Lock, Crown, Medal, Share2, Copy, CheckCircle, Settings, BarChart3 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useTournament } from "../context/TournamentContext";
import { useLanguage } from "../context/LanguageContext";
import MatchPlayerStats from "../components/MatchPlayerStats";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import ExportButton from "../components/ExportButton";
import LanguageSelector from "../components/LanguageSelector";

export default function BracketPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTournament, loadTournament, updateBracketScore, resetMatch, isCreator } = useTournament();
  const { t } = useLanguage();
  const bracketRef = useRef(null);

  const [selectedMatch, setSelectedMatch] = useState(null);
  const [scoreA, setScoreA] = useState("");
  const [scoreB, setScoreB] = useState("");
  const [selectedBracketType, setSelectedBracketType] = useState("winners");
  const [matchStats, setMatchStats] = useState([]);
  const [statsValid, setStatsValid] = useState(true);
  const handleStatsChange = useCallback((combined, valid) => {
    setMatchStats(combined);
    setStatsValid(valid);
  }, []);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Generate share link
  const shareLink = typeof window !== 'undefined' ? `${window.location.origin}/t/${id}` : '';

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      toast.success(t('toast.linkCopied'));
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      toast.error(t('toast.linkCopyFailed'));
    }
  };

  useEffect(() => {
    if (id) {
      const loadData = async () => {
        const tournament = await loadTournament(id);
        if (!tournament) {
          toast.error(t('toast.tournamentNotFound'));
          navigate("/");
        }
      };
      loadData();
    }
  }, [id, loadTournament, navigate]);

  if (!currentTournament) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-white/60">{t('loading')}</div>
      </div>
    );
  }

  const { matches, rounds, losersMatches, losersRounds, participants, champion, hasLosersLeague, hasBronzeMatch, bronzeMatch, hasPlayerStats } = currentTournament;
  const numParticipants = participants?.length || 0;
  const isDoubleElimination = hasLosersLeague && losersMatches?.length > 0;
  const hasBronze = hasBronzeMatch && bronzeMatch;

  // Calculate card size based on number of participants
  const getCardSize = () => {
    if (numParticipants <= 4) return { width: 'w-56', padding: 'p-3', text: 'text-sm', gap: '12px' };
    if (numParticipants <= 8) return { width: 'w-48', padding: 'p-2', text: 'text-xs', gap: '8px' };
    if (numParticipants <= 16) return { width: 'w-44', padding: 'p-2', text: 'text-xs', gap: '6px' };
    return { width: 'w-40', padding: 'p-1.5', text: 'text-[10px]', gap: '4px' };
  };

  const cardSize = getCardSize();

  // Group matches by round
  const matchesByRound = rounds?.map((round, idx) => ({
    ...round,
    roundIndex: idx,
    matches: matches?.filter((m) => m.round === idx) || [],
  })) || [];

  // Group losers matches by round
  const losersMatchesByRound = losersRounds?.map((round, idx) => ({
    ...round,
    roundIndex: idx,
    matches: losersMatches?.filter((m) => m.round === idx) || [],
  })) || [];

  // Calculate games remaining per round
  const getGamesRemaining = (roundMatches) => {
    return roundMatches.filter(m => m.status !== 'completed').length;
  };

  const openScoreDialog = (match, bracketType = 'winners') => {
    if (!isCreator) {
      toast.info(t('toast.viewOnly'));
      return;
    }
    if (match.status === "waiting" || match.participantA?.isBye || match.participantB?.isBye) {
      return;
    }
    setSelectedMatch(match);
    setSelectedBracketType(bracketType);
    setScoreA(match.scoreA !== null && match.scoreA !== 'W' ? String(match.scoreA) : "");
    setScoreB(match.scoreB !== null && match.scoreB !== 'W' ? String(match.scoreB) : "");
  };

  const handleSubmitScore = async () => {
    if (!selectedMatch) return;
    if (!isCreator) {
      toast.error(t('toast.noPermission'));
      return;
    }

    // Default empty scores to 0
    const numScoreA = scoreA === "" ? 0 : parseInt(scoreA, 10);
    const numScoreB = scoreB === "" ? 0 : parseInt(scoreB, 10);

    if (isNaN(numScoreA) || isNaN(numScoreB)) {
      toast.error(t('toast.enterValidScores'));
      return;
    }

    if (numScoreA < 0 || numScoreB < 0) {
      toast.error(t('toast.scoresCannotBeNegative'));
      return;
    }

    if (numScoreA === numScoreB) {
      toast.error(t('toast.scoresCannotBeTied'));
      return;
    }

    if (hasPlayerStats && !statsValid) {
      toast.error(t('stats.goalsMustEqualScore'));
      return;
    }

    await updateBracketScore(selectedMatch.id, numScoreA, numScoreB, selectedBracketType, hasPlayerStats ? matchStats : null);
    setSelectedMatch(null);
    setScoreA("");
    setScoreB("");
    setMatchStats([]);
    setStatsValid(true);
    toast.success(t('toast.scoreRecorded'));
  };

  const handleResetMatch = async (match, bracketType = 'winners') => {
    if (!isCreator) {
      toast.info(t('toast.viewOnlyReset'));
      return;
    }
    await resetMatch(match.id, bracketType);
    toast.success(t('toast.matchReset'));
  };

  // Calculate final rankings based on elimination order
  const calculateFinalRankings = () => {
    if (!participants || participants.length === 0) return [];
    
    const rankings = participants.map(p => ({
      ...p,
      rank: 0,
      points: 0,
      wins: 0,
      losses: 0,
      eliminated: false,
      eliminatedAt: null,
      isBronzeWinner: false,
    }));
    
    // Process all matches to calculate wins/losses
    const allMatches = [...(matches || []), ...(losersMatches || [])];
    
    allMatches.forEach(match => {
      if (match.status === 'completed' && match.winner && match.loser) {
        const winner = rankings.find(p => p.id === match.winner.id);
        const loser = rankings.find(p => p.id === match.loser.id);
        
        if (winner) {
          winner.wins++;
          winner.points += 3; // 3 points per win
        }
        if (loser) {
          loser.losses++;
          // In double elimination, track when eliminated (2 losses)
          if (isDoubleElimination && loser.losses >= 2) {
            loser.eliminated = true;
            if (!loser.eliminatedAt) loser.eliminatedAt = match.round;
          } else if (!isDoubleElimination && loser.losses >= 1) {
            loser.eliminated = true;
            if (!loser.eliminatedAt) loser.eliminatedAt = match.round;
          }
        }
      }
    });
    
    // Process Grand Final if exists
    if (currentTournament.grandFinal?.status === 'completed') {
      const gf = currentTournament.grandFinal;
      const winner = rankings.find(p => p.id === gf.winner?.id);
      const loser = rankings.find(p => p.id === gf.loser?.id);
      if (winner) {
        winner.wins++;
        winner.points += 5; // Bonus for Grand Final win
      }
      if (loser) {
        loser.losses++;
        loser.eliminated = true;
      }
    }
    
    // Process Bronze Medal Match if exists
    if (bronzeMatch?.status === 'completed') {
      const winner = rankings.find(p => p.id === bronzeMatch.winner?.id);
      const loser = rankings.find(p => p.id === bronzeMatch.loser?.id);
      if (winner) {
        winner.wins++;
        winner.points += 2; // Bonus for bronze win
        winner.isBronzeWinner = true;
      }
      if (loser) {
        loser.losses++;
      }
    }
    
    // Double elimination: rank by losses (asc) then wins (desc); points break ties.
    // Other bracket formats: rank by points (desc), then wins (desc), then losses (asc).
    rankings.sort((a, b) => {
      if (isDoubleElimination) {
        if (a.losses !== b.losses) return a.losses - b.losses;
        if (b.wins !== a.wins) return b.wins - a.wins;
        return b.points - a.points;
      }
      if (b.points !== a.points) return b.points - a.points;
      if (b.wins !== a.wins) return b.wins - a.wins;
      return a.losses - b.losses;
    });
    
    // Assign ranks
    rankings.forEach((p, idx) => {
      p.rank = idx + 1;
    });
    
    return rankings;
  };

  // Render a single bracket (used for both winners and losers)
  const renderBracket = (bracketRounds, bracketType, title, bgColor) => {
    const totalGamesRemaining = bracketRounds.reduce((sum, round) => sum + getGamesRemaining(round.matches), 0);
    
    // Calculate base spacing - use first round match count for reference
    const firstRoundMatches = bracketRounds[0]?.matches?.length || 1;
    const baseGap = parseInt(cardSize.gap) || 8;

    return (
      <div className={`${bgColor} rounded-lg p-4 mb-4`}>
        {/* Bracket Title */}
        <div className="flex items-center justify-between mb-4">
          <h3 className={`font-heading text-lg uppercase tracking-wide ${
            bracketType === 'winners' ? 'text-neon-cyan' : 'text-orange-400'
          }`}>
            {title}
          </h3>
          <Badge className={`${bracketType === 'winners' ? 'bg-neon-cyan/20 text-neon-cyan' : 'bg-orange-500/20 text-orange-400'}`}>
            {totalGamesRemaining} {totalGamesRemaining === 1 ? t('bracket.gameLeft') : t('bracket.gamesLeft')}
          </Badge>
        </div>

        {/* Bracket Grid */}
        <div className="flex gap-3 overflow-x-auto pb-2">
          {bracketRounds.map((round, roundIndex) => {
            const gamesRemaining = getGamesRemaining(round.matches);
            const matchCount = round.matches?.length || 1;
            
            // Calculate spacing based on how many matches should fit in the same vertical space
            // This ensures consistent spacing between brackets
            const spacingMultiplier = Math.max(1, firstRoundMatches / matchCount);
            const paddingTop = roundIndex > 0 ? (spacingMultiplier - 1) * 30 : 0;
            const gap = roundIndex > 0 ? (spacingMultiplier * 2 - 1) * 30 : baseGap;
            
            return (
              <div key={round.name || `round-${roundIndex}`} className="flex flex-col min-w-fit">
                {/* Round Header with Games Remaining */}
                <div className="text-center mb-3">
                  <h4 className={`font-heading uppercase tracking-wide text-xs ${
                    round.name.includes('Final') ? 'text-neon-green' : 
                    round.name.includes('Semi') ? 'text-neon-cyan' : 
                    round.name.includes('Quarter') ? 'text-purple-400' : 
                    'text-white/50'
                  }`}>
                    {round.name}
                  </h4>
                  <span className={`text-[10px] font-data ${gamesRemaining > 0 ? 'text-yellow-400' : 'text-neon-green'}`}>
                    {gamesRemaining > 0 ? `${gamesRemaining} game${gamesRemaining > 1 ? 's' : ''} left` : '✓ Complete'}
                  </span>
                </div>

                {/* Matches */}
                <div
                  className="flex flex-col justify-around flex-1"
                  style={{
                    paddingTop: `${paddingTop}px`,
                    gap: `${gap}px`,
                  }}
                >
                  {round.matches?.map((match, matchIndex) => {
                    const isCompleted = match.status === "completed";
                    const isReady = match.status === "pending";
                    const hasBye = match.participantA?.isBye || match.participantB?.isBye;
                    const isFinal = round.name.includes('Final');
                    const canClick = isCreator && (isReady || isCompleted) && !hasBye;

                    return (
                      <div key={match.id} className="relative flex items-center">
                        {/* Match Card */}
                        <div
                          onClick={() => openScoreDialog(match, bracketType)}
                          className={`relative ${cardSize.width} border-l-2 ${cardSize.padding} transition-all duration-200 ${
                            isCompleted
                              ? "border-l-neon-green bg-neon-green/5"
                              : isReady && !hasBye
                              ? `border-l-${bracketType === 'winners' ? 'neon-cyan' : 'orange-400'}`
                              : "border-l-white/20"
                          } bg-black/40 ${canClick ? "cursor-pointer hover:bg-white/5" : "cursor-default"}`}
                          data-testid={`match-card-${match.id}`}
                        >
                          {/* Match Number */}
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white/30 text-[9px] font-data">
                              M{match.matchIndex + 1}
                            </span>
                            {isCompleted && !hasBye && isCreator && (
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`reset-match-${match.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResetMatch(match, bracketType);
                                }}
                                className="w-4 h-4 text-white/30 hover:text-red-400"
                              >
                                <RotateCcw className="w-2.5 h-2.5" />
                              </Button>
                            )}
                          </div>

                          {/* Participant A */}
                          <div className={`flex items-center justify-between ${cardSize.text} ${
                            match.winner?.id === match.participantA?.id ? "text-neon-green" :
                            match.participantA?.isBye ? "text-white/20" : "text-white"
                          }`}>
                            <div className="flex items-center gap-1 flex-1 min-w-0">
                              {match.participantA?.logo && (
                                <img src={match.participantA.logo} alt="" className="w-4 h-4 rounded object-cover flex-shrink-0" />
                              )}
                              {match.winner?.id === match.participantA?.id && (
                                <ChevronRight className="w-2.5 h-2.5 text-neon-green flex-shrink-0" />
                              )}
                              <span className={`font-heading uppercase truncate ${
                                match.winner?.id === match.participantA?.id ? "font-bold" : ""
                              }`}>
                                {match.participantA?.name || "TBD"}
                              </span>
                            </div>
                            {isCompleted && match.scoreA !== null && (
                              <span className="font-data font-bold ml-1">{match.scoreA}</span>
                            )}
                          </div>

                          <div className="border-t border-white/10 my-0.5" />

                          {/* Participant B */}
                          <div className={`flex items-center justify-between ${cardSize.text} ${
                            match.winner?.id === match.participantB?.id ? "text-neon-green" :
                            match.participantB?.isBye ? "text-white/20" : "text-white"
                          }`}>
                            <div className="flex items-center gap-1 flex-1 min-w-0">
                              {match.participantB?.logo && (
                                <img src={match.participantB.logo} alt="" className="w-4 h-4 rounded object-cover flex-shrink-0" />
                              )}
                              {match.winner?.id === match.participantB?.id && (
                                <ChevronRight className="w-2.5 h-2.5 text-neon-green flex-shrink-0" />
                              )}
                              <span className={`font-heading uppercase truncate ${
                                match.winner?.id === match.participantB?.id ? "font-bold" : ""
                              }`}>
                                {match.participantB?.name || "TBD"}
                              </span>
                            </div>
                            {isCompleted && match.scoreB !== null && (
                              <span className="font-data font-bold ml-1">{match.scoreB}</span>
                            )}
                          </div>

                          {/* Ready indicator */}
                          {isReady && !hasBye && isCreator && (
                            <div className="absolute top-0.5 right-0.5">
                              <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                                bracketType === 'winners' ? 'bg-neon-cyan' : 'bg-orange-400'
                              }`} />
                            </div>
                          )}

                          {/* Final winner trophy */}
                          {isFinal && isCompleted && (
                            <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-neon-green rounded-full flex items-center justify-center">
                              <Trophy className="w-3 h-3 text-black" />
                            </div>
                          )}
                        </div>

                        {/* Connector Lines */}
                        {roundIndex < bracketRounds.length - 1 && (
                          <div className="relative w-4 h-full">
                            <div className={`absolute left-0 top-1/2 w-2 h-px ${
                              isCompleted ? 'bg-neon-green/50' : 'bg-white/20'
                            }`} />
                            {matchIndex % 2 === 0 && round.matches.length > 1 && (
                              <div className={`absolute left-2 top-1/2 w-px ${
                                isCompleted ? 'bg-neon-green/50' : 'bg-white/20'
                              }`} style={{ height: `${Math.pow(2, roundIndex) * 30 + 10}px` }} />
                            )}
                            {matchIndex % 2 === 1 && (
                              <div className={`absolute left-2 bottom-1/2 w-px ${
                                isCompleted ? 'bg-neon-green/50' : 'bg-white/20'
                              }`} style={{ height: `${Math.pow(2, roundIndex) * 30 + 10}px` }} />
                            )}
                            <div className={`absolute left-2 w-2 h-px ${
                              isCompleted ? 'bg-neon-green/50' : 'bg-white/20'
                            }`} style={{
                              top: matchIndex % 2 === 0 
                                ? `calc(50% + ${Math.pow(2, roundIndex) * 15 + 5}px)`
                                : `calc(50% - ${Math.pow(2, roundIndex) * 15 + 5}px)`,
                            }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

    return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Button
                data-testid="back-to-home-btn"
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="text-white/60 hover:text-white hover:bg-white/10 flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h1 className="font-heading text-lg font-bold tracking-tight text-white uppercase truncate">
                    {currentTournament.name}
                  </h1>
                  {champion && (
                    <Badge className="bg-neon-green/20 text-neon-green border-neon-green/30 flex items-center gap-1 text-xs flex-shrink-0">
                      <Crown className="w-3 h-3" />
                      {t('home.completed')}
                    </Badge>
                  )}
                </div>
                <p className="text-white/40 text-xs font-data hidden sm:block">
                  {currentTournament.sportType} • {numParticipants} {t('home.players')} • {isDoubleElimination ? t('setup.doubleElimination') : hasBronze ? t('setup.bronzeMedal') : t('setup.bracket')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <LanguageSelector />
              {hasPlayerStats && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/stats/${currentTournament.id}`)}
                  className="border-neon-purple/30 text-neon-purple hover:bg-neon-purple/10 gap-2"
                  data-testid="player-stats-btn"
                >
                  <BarChart3 className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('stats.playerStats')}</span>
                </Button>
              )}
              {isCreator && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/setup?edit=${currentTournament.id}`)}
                  className="border-white/20 text-white/80 hover:bg-white/10 gap-2"
                  data-testid="edit-tournament-btn"
                >
                  <Settings className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('setup.editTournament')}</span>
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowShareDialog(true)}
                className="border-neon-cyan/30 text-neon-cyan hover:bg-neon-cyan/10 gap-2"
                data-testid="share-tournament-btn"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">{t('share')}</span>
              </Button>
              <ExportButton 
                targetRef={bracketRef} 
                filename={`${currentTournament.name}-bracket`}
                title={t('export')}
              />
              <Badge
                onClick={() => {
                  navigator.clipboard?.writeText(currentTournament.code);
                  toast.success(t('toast.codeCopied'));
                }}
                data-testid="copy-code-badge"
                title={t('toast.codeCopied')}
                className="bg-white/10 text-white/60 border-white/20 font-data text-xs flex flex-shrink-0 items-center gap-1 cursor-pointer hover:bg-white/20 hover:text-white transition-colors"
              >
                {currentTournament.code}
                <Copy className="w-3 h-3" />
              </Badge>
              {!isCreator && (
                <Badge data-testid="view-only-badge" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  <span className="hidden sm:inline">{t('bracket.viewOnly')}</span>
                </Badge>
              )}
              {champion && (
                <Button
                  onClick={() => navigate(`/champion/${currentTournament.id}`)}
                  className="bg-neon-green/20 text-neon-green hover:bg-neon-green/30 border border-neon-green/30"
                  size="sm"
                >
                  <Trophy className="w-3 h-3 sm:mr-1" />
                  <span className="hidden sm:inline">{t('bracket.champion')}</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Champion Banner */}
      {champion && (
        <div className="bg-gradient-to-r from-neon-green/10 via-neon-green/5 to-transparent border-b border-neon-green/20 py-2 px-4">
          <div className="container mx-auto flex items-center gap-2">
            <Trophy className="w-4 h-4 text-neon-green" />
            <span className="text-neon-green font-heading uppercase text-sm">{t('bracket.champion')}:</span>
            {champion.logo && (
              <img src={champion.logo} alt="" className="w-5 h-5 rounded object-cover" />
            )}
            <span className="text-white font-heading font-bold uppercase text-sm">{champion.name}</span>
          </div>
        </div>
      )}

      {/* Brackets */}
      <main ref={bracketRef} className="p-2 sm:p-4 overflow-x-auto">
        {/* Winners Bracket */}
        {renderBracket(matchesByRound, 'winners', t('bracket.winnersBracket'), 'bg-neon-cyan/5')}

        {/* Losers Bracket (Double Elimination only) */}
        {isDoubleElimination && losersMatchesByRound.length > 0 && (
          renderBracket(losersMatchesByRound, 'losers', t('bracket.losersBracket'), 'bg-orange-500/5')
        )}

        {/* Grand Final (Double Elimination only) */}
        {hasLosersLeague && currentTournament.grandFinal && (
          <div className="bg-gradient-to-r from-neon-purple/10 via-neon-purple/5 to-transparent rounded-lg p-4 mt-4 border border-neon-purple/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg uppercase tracking-wide text-neon-purple flex items-center gap-2">
                <Crown className="w-5 h-5" />
                {t('bracket.grandFinal')}
              </h3>
              <Badge className={`${currentTournament.grandFinal.status === 'completed' ? 'bg-neon-green/20 text-neon-green' : 'bg-neon-purple/20 text-neon-purple'}`}>
                {currentTournament.grandFinal.status === 'completed' ? '✓ ' + t('home.completed') : '1 ' + t('bracket.gameLeft')}
              </Badge>
            </div>
            
            <div className="flex justify-center">
              <div
                onClick={() => isCreator && openScoreDialog(currentTournament.grandFinal, 'grandFinal')}
                className={`relative w-72 border-2 p-4 rounded-lg transition-all duration-200 ${
                  currentTournament.grandFinal.status === 'completed'
                    ? "border-neon-green bg-neon-green/10"
                    : currentTournament.grandFinal.status === 'pending'
                    ? "border-neon-purple bg-neon-purple/10 cursor-pointer hover:bg-neon-purple/20"
                    : "border-white/20 bg-white/5"
                }`}
                data-testid="grand-final-match"
              >
                <div className="text-center mb-2 text-xs text-neon-purple font-heading">
                  {t('bracket.winnersChampVsLosers')}
                </div>
                
                {/* Participant A - Winners Champion */}
                <div className={`flex items-center justify-between py-2 ${
                  currentTournament.grandFinal.winner?.id === currentTournament.grandFinal.participantA?.id 
                    ? "text-neon-green" : "text-white"
                }`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Badge className="bg-neon-cyan/20 text-neon-cyan text-[10px]">W</Badge>
                    {currentTournament.grandFinal.participantA?.logo && (
                      <img src={currentTournament.grandFinal.participantA.logo} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
                    )}
                    <span className="font-heading uppercase truncate">
                      {currentTournament.grandFinal.participantA?.name || "TBD"}
                    </span>
                    {currentTournament.grandFinal.winner?.id === currentTournament.grandFinal.participantA?.id && (
                      <Crown className="w-4 h-4 text-neon-green" />
                    )}
                  </div>
                  {currentTournament.grandFinal.scoreA !== null && (
                    <span className="font-data font-bold text-xl">{currentTournament.grandFinal.scoreA}</span>
                  )}
                </div>
                
                <div className="border-t border-white/20 my-1" />
                
                {/* Participant B - Losers Champion */}
                <div className={`flex items-center justify-between py-2 ${
                  currentTournament.grandFinal.winner?.id === currentTournament.grandFinal.participantB?.id 
                    ? "text-neon-green" : "text-white"
                }`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Badge className="bg-orange-500/20 text-orange-400 text-[10px]">L</Badge>
                    {currentTournament.grandFinal.participantB?.logo && (
                      <img src={currentTournament.grandFinal.participantB.logo} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
                    )}
                    <span className="font-heading uppercase truncate">
                      {currentTournament.grandFinal.participantB?.name || "TBD"}
                    </span>
                    {currentTournament.grandFinal.winner?.id === currentTournament.grandFinal.participantB?.id && (
                      <Crown className="w-4 h-4 text-neon-green" />
                    )}
                  </div>
                  {currentTournament.grandFinal.scoreB !== null && (
                    <span className="font-data font-bold text-xl">{currentTournament.grandFinal.scoreB}</span>
                  )}
                </div>
                
                {currentTournament.grandFinal.status === 'pending' && isCreator && (
                  <div className="absolute top-2 right-2">
                    <div className="w-2 h-2 rounded-full bg-neon-purple animate-pulse" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Grand Final Reset (only when the losers finalist won the first Grand Final) */}
        {hasLosersLeague && currentTournament.grandFinalReset && currentTournament.grandFinalReset.status !== 'waiting' && (
          <div className="bg-gradient-to-r from-red-500/10 via-red-500/5 to-transparent rounded-lg p-4 mt-4 border border-red-500/30" data-testid="grand-final-reset-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg uppercase tracking-wide text-red-400 flex items-center gap-2">
                <Crown className="w-5 h-5" />
                {t('bracket.grandFinalReset')}
              </h3>
              <Badge className={`${currentTournament.grandFinalReset.status === 'completed' ? 'bg-neon-green/20 text-neon-green' : 'bg-red-500/20 text-red-400'}`}>
                {currentTournament.grandFinalReset.status === 'completed' ? '✓ ' + t('home.completed') : '1 ' + t('bracket.gameLeft')}
              </Badge>
            </div>
            <div className="flex justify-center">
              <div
                onClick={() => isCreator && currentTournament.grandFinalReset.status === 'pending' && openScoreDialog(currentTournament.grandFinalReset, 'grandFinalReset')}
                className={`relative w-72 border-2 p-4 rounded-lg transition-all duration-200 ${
                  currentTournament.grandFinalReset.status === 'completed'
                    ? 'border-neon-green bg-neon-green/10'
                    : 'border-red-500 bg-red-500/10 cursor-pointer hover:bg-red-500/20'
                }`}
                data-testid="grand-final-reset-match"
              >
                <div className="text-center mb-2 text-xs text-red-400 font-heading">{t('bracket.grandFinalReset')}</div>
                <div className={`flex items-center justify-between py-2 ${currentTournament.grandFinalReset.winner?.id === currentTournament.grandFinalReset.participantA?.id ? 'text-neon-green' : 'text-white'}`}>
                  <span className="font-heading uppercase truncate flex-1">{currentTournament.grandFinalReset.participantA?.name || 'TBD'}</span>
                  {currentTournament.grandFinalReset.scoreA !== null && (<span className="font-data font-bold text-xl">{currentTournament.grandFinalReset.scoreA}</span>)}
                </div>
                <div className="border-t border-white/20 my-1" />
                <div className={`flex items-center justify-between py-2 ${currentTournament.grandFinalReset.winner?.id === currentTournament.grandFinalReset.participantB?.id ? 'text-neon-green' : 'text-white'}`}>
                  <span className="font-heading uppercase truncate flex-1">{currentTournament.grandFinalReset.participantB?.name || 'TBD'}</span>
                  {currentTournament.grandFinalReset.scoreB !== null && (<span className="font-data font-bold text-xl">{currentTournament.grandFinalReset.scoreB}</span>)}
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Bronze Medal Match (Single Elimination with 3rd place) */}
        {hasBronze && (
          <div className="bg-gradient-to-r from-orange-500/10 via-orange-500/5 to-transparent rounded-lg p-4 mt-4 border border-orange-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg uppercase tracking-wide text-orange-500 flex items-center gap-2">
                <Medal className="w-5 h-5" />
                {t('bracket.bronzeMatch')}
              </h3>
              <Badge className={`${bronzeMatch.status === 'completed' ? 'bg-neon-green/20 text-neon-green' : bronzeMatch.status === 'pending' ? 'bg-orange-500/20 text-orange-400' : 'bg-white/10 text-white/40'}`}>
                {bronzeMatch.status === 'completed' ? '✓ ' + t('home.completed') : bronzeMatch.status === 'pending' ? '1 ' + t('bracket.gameLeft') : t('bracket.waitingForSemis')}
              </Badge>
            </div>
            
            <div className="flex justify-center">
              <div
                onClick={() => bronzeMatch.status === 'pending' && isCreator && openScoreDialog(bronzeMatch, 'bronze')}
                className={`relative w-72 border-2 p-4 rounded-lg transition-all duration-200 ${
                  bronzeMatch.status === 'completed'
                    ? "border-neon-green bg-neon-green/10"
                    : bronzeMatch.status === 'pending'
                    ? "border-orange-500 bg-orange-500/10 cursor-pointer hover:bg-orange-500/20"
                    : "border-white/20 bg-white/5"
                }`}
                data-testid="bronze-medal-match"
              >
                <div className="text-center mb-2 text-xs text-orange-400 font-heading">
                  {t('bracket.thirdPlace')}
                </div>
                
                {/* Participant A */}
                <div className={`flex items-center justify-between py-2 ${
                  bronzeMatch.winner?.id === bronzeMatch.participantA?.id 
                    ? "text-neon-green" : "text-white"
                }`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {bronzeMatch.participantA?.logo && (
                      <img src={bronzeMatch.participantA.logo} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
                    )}
                    <span className="font-heading uppercase truncate">
                      {bronzeMatch.participantA?.name || "TBD"}
                    </span>
                    {bronzeMatch.winner?.id === bronzeMatch.participantA?.id && (
                      <Medal className="w-4 h-4 text-orange-500" />
                    )}
                  </div>
                  {bronzeMatch.scoreA !== null && (
                    <span className="font-data font-bold text-xl">{bronzeMatch.scoreA}</span>
                  )}
                </div>
                
                <div className="border-t border-white/20 my-1" />
                
                {/* Participant B */}
                <div className={`flex items-center justify-between py-2 ${
                  bronzeMatch.winner?.id === bronzeMatch.participantB?.id 
                    ? "text-neon-green" : "text-white"
                }`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {bronzeMatch.participantB?.logo && (
                      <img src={bronzeMatch.participantB.logo} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
                    )}
                    <span className="font-heading uppercase truncate">
                      {bronzeMatch.participantB?.name || "TBD"}
                    </span>
                    {bronzeMatch.winner?.id === bronzeMatch.participantB?.id && (
                      <Medal className="w-4 h-4 text-orange-500" />
                    )}
                  </div>
                  {bronzeMatch.scoreB !== null && (
                    <span className="font-data font-bold text-xl">{bronzeMatch.scoreB}</span>
                  )}
                </div>
                
                {bronzeMatch.status === 'pending' && isCreator && (
                  <div className="absolute top-2 right-2">
                    <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Final Rankings - Show when tournament is complete */}
        {champion && (
          <div className="bg-gradient-to-b from-white/5 to-transparent rounded-lg p-4 mt-4 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg uppercase tracking-wide text-white flex items-center gap-2">
                <Trophy className="w-5 h-5 text-neon-green" />
                {t('bracket.finalStandings')}
              </h3>
              <Badge className="bg-neon-green/20 text-neon-green">{t('champion.tournamentComplete')}</Badge>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-3 text-white/60 text-xs font-heading uppercase">{t('bracket.rank')}</th>
                    <th className="text-left py-2 px-3 text-white/60 text-xs font-heading uppercase">{t('bracket.player')}</th>
                    <th className="text-center py-2 px-3 text-white/60 text-xs font-heading uppercase">{t('bracket.wins')}</th>
                    <th className="text-center py-2 px-3 text-white/60 text-xs font-heading uppercase">{t('bracket.losses')}</th>
                    <th className="text-center py-2 px-3 text-white/60 text-xs font-heading uppercase">{t('bracket.points')}</th>
                  </tr>
                </thead>
                <tbody>
                  {calculateFinalRankings().map((player, idx) => (
                    <tr 
                      key={player.id} 
                      className={`border-b border-white/5 ${
                        idx === 0 ? 'bg-neon-green/10' : 
                        idx === 1 ? 'bg-neon-cyan/5' : 
                        idx === 2 ? 'bg-orange-500/5' : ''
                      }`}
                    >
                      <td className="py-3 px-3">
                        <div className={`flex items-center justify-center w-7 h-7 rounded-full font-heading font-bold ${
                          idx === 0 ? 'bg-neon-green text-black' :
                          idx === 1 ? 'bg-neon-cyan text-black' :
                          idx === 2 ? 'bg-orange-500 text-black' :
                          'bg-white/10 text-white/60'
                        }`}>
                          {player.rank}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          {player.logo && (
                            <img src={player.logo} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
                          )}
                          <span className={`font-heading uppercase ${idx === 0 ? 'text-neon-green' : 'text-white'}`}>
                            {player.name}
                          </span>
                          {idx === 0 && <Crown className="w-4 h-4 text-neon-green" />}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className="text-neon-green font-data">{player.wins}</span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className="text-red-400 font-data">{player.losses}</span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className="text-white font-data font-bold">{player.points}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Score Dialog */}
      <Dialog open={!!selectedMatch && isCreator} onOpenChange={() => setSelectedMatch(null)}>
        <DialogContent className="bg-surface-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase text-white">
              {t('bracket.enterScores')}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {selectedMatch?.roundName}
            </DialogDescription>
          </DialogHeader>

          {selectedMatch && (
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="font-heading text-lg uppercase text-white">
                    {selectedMatch.participantA?.name || t('bracket.tbd')}
                  </p>
                </div>
                <Input
                  data-testid="score-input-a"
                  type="number"
                  min="0"
                  value={scoreA}
                  onChange={(e) => setScoreA(e.target.value)}
                  className="w-20 input-dark text-center text-2xl font-data"
                  placeholder="0"
                />
              </div>

              <div className="text-center text-white/30 font-heading">{t('bracket.vs')}</div>

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="font-heading text-lg uppercase text-white">
                    {selectedMatch.participantB?.name || t('bracket.tbd')}
                  </p>
                </div>
                <Input
                  data-testid="score-input-b"
                  type="number"
                  min="0"
                  value={scoreB}
                  onChange={(e) => setScoreB(e.target.value)}
                  className="w-20 input-dark text-center text-2xl font-data"
                  placeholder="0"
                />
              </div>

              {hasPlayerStats && (
                <div className="border-t border-white/10 pt-4">
                  <MatchPlayerStats
                    key={selectedMatch.id}
                    participantA={selectedMatch.participantA}
                    participantB={selectedMatch.participantB}
                    scoreA={scoreA}
                    scoreB={scoreB}
                    existing={currentTournament.playerStats?.[selectedMatch.id]}
                    onChange={handleStatsChange}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setSelectedMatch(null)}
              className="text-white/60 hover:text-white"
            >
              {t('cancel')}
            </Button>
            <Button
              data-testid="submit-score-btn"
              onClick={handleSubmitScore}
              className="bg-neon-cyan text-black font-heading uppercase tracking-wide hover:bg-neon-cyan/90"
            >
              <Check className="w-4 h-4 mr-2" />
              {t('confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-surface-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase text-white flex items-center gap-2">
              <Share2 className="w-5 h-5 text-neon-cyan" />
              {t('shareDialog.title')}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {t('shareDialog.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-white/60 text-sm font-heading uppercase">{t('shareDialog.shareLink')}</label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={shareLink}
                  className="input-dark font-data text-sm"
                  data-testid="share-link-input"
                />
                <Button
                  onClick={copyShareLink}
                  className={`px-4 ${linkCopied ? 'bg-neon-green text-black' : 'bg-neon-cyan text-black'} hover:opacity-90`}
                  data-testid="copy-link-btn"
                >
                  {linkCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* QR Code */}
            <div className="flex justify-center py-4">
              <div className="bg-white p-3 rounded-lg">
                <QRCodeSVG value={shareLink} size={150} level="M" data-testid="share-qr-code" />
              </div>
            </div>

            <div className="border-t border-white/10 pt-4">
              <p className="text-white/40 text-xs">
                {t('shareDialog.orShare')}
              </p>
              <div className="flex gap-4 mt-2">
                <div>
                  <span className="text-white/60 text-xs">{t('shareDialog.name')}:</span>
                  <p className="text-white font-heading uppercase">{currentTournament.name}</p>
                </div>
                <div>
                  <span className="text-white/60 text-xs">{t('shareDialog.code')}:</span>
                  <p className="text-neon-cyan font-data font-bold">{currentTournament.code}</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowShareDialog(false)}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              {t('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
